import { useState } from "react";
import SplitFlex from "./SplitFlex";

## Basic Horizontal Split

<div style={{ height: "200px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.4} style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#f5f5f5", height: "100%" }}>
      <h4 style={{ margin: 0 }}>Left Panel (40%)</h4>
      <p style={{ fontSize: "14px", color: "#666" }}>Drag separator to resize</p>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <h4 style={{ margin: 0 }}>Right Panel (60%)</h4>
      <p style={{ fontSize: "14px", color: "#666" }}>Flex-based layout</p>
    </div>
  </SplitFlex>
</div>

---

## Vertical Split

<div style={{ height: "300px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="vertical" sizeRatio={0.3} style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#f5f5f5", height: "100%" }}>
      <h4 style={{ margin: 0 }}>Top Panel</h4>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <h4 style={{ margin: 0 }}>Bottom Panel</h4>
      <p style={{ fontSize: "14px", color: "#666" }}>Takes remaining space</p>
    </div>
  </SplitFlex>
</div>

---

## Equal Split (50/50)

<div style={{ height: "150px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.5} style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#e8f4fd", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      Left (50%)
    </div>
    <div style={{ padding: "1rem", background: "#fdf8e8", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      Right (50%)
    </div>
  </SplitFlex>
</div>

---

## Non-Resizable

<div style={{ height: "150px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.3} canResize={false} style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#f0f0f0", height: "100%" }}>
      <p style={{ margin: 0 }}>Fixed width panel</p>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <p style={{ margin: 0 }}>Cannot drag to resize</p>
    </div>
  </SplitFlex>
</div>

---

## Minimum Panel Sizes

<div style={{ height: "150px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.5} firstMinSize={100} lastMinSize={100} style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#fafafa", height: "100%" }}>
      <p style={{ margin: 0, fontSize: "14px" }}>Min 100px</p>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <p style={{ margin: 0, fontSize: "14px" }}>Min 100px</p>
    </div>
  </SplitFlex>
</div>

---

## Hide Side

export function HideSideDemo() {
  const [hideSide, setHideSide] = useState(undefined);
  return (
    <div>
      <div style={{ marginBottom: "0.5rem", display: "flex", gap: "0.5rem" }}>
        <button onClick={() => setHideSide(undefined)}>Show Both</button>
        <button onClick={() => setHideSide("first")}>Hide Left</button>
        <button onClick={() => setHideSide("last")}>Hide Right</button>
      </div>
      <div style={{ height: "150px", border: "1px dashed #ccc" }}>
        <SplitFlex direction="horizontal" sizeRatio={0.4} hideSide={hideSide} style={{ height: "100%" }}>
          <div style={{ padding: "1rem", background: "#e8f4fd", height: "100%" }}>
            <p style={{ margin: 0 }}>Left Panel</p>
          </div>
          <div style={{ padding: "1rem", background: "#fdf8e8", height: "100%" }}>
            <p style={{ margin: 0 }}>Right Panel</p>
          </div>
        </SplitFlex>
      </div>
    </div>
  );
}

<HideSideDemo />

---

## Responsive Layout

The responsive prop switches horizontal layouts to vertical on portrait orientations.

<div style={{ height: "200px", border: "1px dashed #ccc" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.35} responsive style={{ height: "100%" }}>
    <div style={{ padding: "1rem", background: "#f0f9ff", height: "100%" }}>
      <h4 style={{ margin: "0 0 0.5rem" }}>Sidebar</h4>
      <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
        Becomes top panel on narrow screens
      </p>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <h4 style={{ margin: "0 0 0.5rem" }}>Main Content</h4>
      <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
        Primary content area
      </p>
    </div>
  </SplitFlex>
</div>

---

## Dashboard Layout Example

<div style={{ height: "300px", border: "1px solid #e5e5e5", borderRadius: "8px", overflow: "hidden" }}>
  <SplitFlex direction="horizontal" sizeRatio={0.25} firstMinSize={180} lastMinSize={300} style={{ height: "100%" }}>
    <div style={{ background: "#fafafa", height: "100%", borderRight: "1px solid #e5e5e5" }}>
      <div style={{ padding: "1rem", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
        Navigation
      </div>
      <div style={{ padding: "0.5rem" }}>
        <div style={{ padding: "0.5rem", background: "#e5e5e5", borderRadius: "4px", marginBottom: "0.25rem" }}>Dashboard</div>
        <div style={{ padding: "0.5rem" }}>Analytics</div>
        <div style={{ padding: "0.5rem" }}>Reports</div>
        <div style={{ padding: "0.5rem" }}>Settings</div>
      </div>
    </div>
    <div style={{ padding: "1rem", height: "100%" }}>
      <h3 style={{ margin: "0 0 1rem" }}>Dashboard</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem" }}>
        <div style={{ background: "#f5f5f5", padding: "1rem", borderRadius: "4px" }}>
          <div style={{ fontSize: "24px", fontWeight: 600 }}>1,234</div>
          <div style={{ color: "#666", fontSize: "14px" }}>Total Users</div>
        </div>
        <div style={{ background: "#f5f5f5", padding: "1rem", borderRadius: "4px" }}>
          <div style={{ fontSize: "24px", fontWeight: 600 }}>567</div>
          <div style={{ color: "#666", fontSize: "14px" }}>Active Today</div>
        </div>
      </div>
    </div>
  </SplitFlex>
</div>


